class MobileCollapse extends HTMLElement {
  constructor() {
    super();
    this.mediaQuery = window.matchMedia('(max-width: 991px)');
    this._details = null;
    this._summary = null;
    this._content = null;
    this._icon = null;
    this._originalHTML = this.innerHTML;

    this._opened = `<path d="M432 256c0 17.7-14.3 32-32 32L48 288c-17.7 0-32-14.3-32-32s14.3-32 32-32l352 0c17.7 0 32 14.3 32 32z"/>`;
    this._closed = `<path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 144L48 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l144 0 0 144c0 17.7 14.3 32 32 32s32-14.3 32-32l0-144 144 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-144 0 0-144z"/>`;

    this._handleMediaChange = this._handleMediaChange.bind(this);
    this._onResize = this._onResize.bind(this);
  }

  connectedCallback() {
    this._title = this.querySelector('.title');
    this._hr = this.querySelector('.title + hr');
    if (!this._title) {
      console.warn('<mobile-collapse> requires an element with class ".title"');
      return;
    }

    this.mediaQuery.addEventListener('change', this._handleMediaChange);
    this._handleMediaChange(this.mediaQuery);
  }

  disconnectedCallback() {
    this.mediaQuery.removeEventListener('change', this._handleMediaChange);
    window.removeEventListener('resize', this._onResize);
  }

  _handleMediaChange(e) {
    if (e.matches) {
      this._setupMobile();
    } else {
      this._teardownMobile();
    }
  }

  _setupMobile() {
    if (this._details) return;

    const details = document.createElement('details');

    const summary = document.createElement('summary');

    this._title.textContent += ':';
    summary.appendChild(this._title);
    if (this._hr) summary.appendChild(this._hr);

    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    icon.setAttribute('viewBox', '0 0 448 512');
    icon.style.fill = 'currentColor';
    icon.style.width = '1em';
    icon.style.height = '1em';
    icon.innerHTML = this._closed;
    summary.appendChild(icon);
    this._icon = icon;

    const contentWrapper = document.createElement('div');
    contentWrapper.classList.add('collapse-content-wrapper');
    contentWrapper.style.transition = 'max-height 0.4s ease, opacity 0.4s ease';
    contentWrapper.style.maxHeight = '0';
    contentWrapper.style.opacity = '0';

    [...this.children].forEach((child) => {
      if (child !== this._title && child !== this._hr) {
        contentWrapper.appendChild(child);
      }
    });

    details.appendChild(summary);
    details.appendChild(contentWrapper);

    this.innerHTML = '';
    this.appendChild(details);

    this._details = details;
    this._summary = summary;
    this._content = contentWrapper;

    this._summary.addEventListener('click', (e) => {
      e.preventDefault();
      this._toggle();
    });

    window.addEventListener('resize', this._onResize);

    // Always open after page fully loads
    this._waitForContentAndOpen();

    this._content.addEventListener('truncate-open', this._onResize);
    this._content.addEventListener('truncate-close', this._onResize);
  }

  _teardownMobile() {
    if (!this._details) return;

    window.removeEventListener('resize', this._onResize);
    this.innerHTML = this._originalHTML;

    this._details = null;
    this._summary = null;
    this._content = null;
    this._icon = null;
    this._title = this.querySelector('.title');
    this._hr = this.querySelector('.title + hr');

    this._content?.removeEventListener('truncate-open', this._onResize);
    this._content?.removeEventListener('truncate-close', this._onResize);
  }

  _toggle() {
    if (this._details.hasAttribute('open')) {
      this._close();
    } else {
      this._open();
    }
  }

  _open(skipAnim = false) {
    this._details.classList.remove('closing');
    this._details.classList.add('open');
    this._details.setAttribute('open', '');

    const scrollHeight = this._content.scrollHeight;
    requestAnimationFrame(() => {
      this._content.style.maxHeight = scrollHeight + 'px';
      this._content.style.opacity = '1';
      this._icon.innerHTML = this._opened;
    });

    if (skipAnim) {
      this._content.style.transition = 'none';
      requestAnimationFrame(() => {
        this._content.style.transition = 'max-height 0.4s ease, opacity 0.4s ease';
      });
    }
  }

  _close() {
    this._details.classList.remove('open');
    this._details.classList.add('closing');
    this._content.style.maxHeight = '0';
    this._content.style.opacity = '0';
    this._icon.innerHTML = this._closed;

    setTimeout(() => {
      this._details.removeAttribute('open');
      this._details.classList.remove('closing');
    }, 400);
  }

  _onResize() {
    if (this._details?.hasAttribute('open') && this._content) {
      this._content.style.maxHeight = this._content.scrollHeight + 'px';
    }
  }

  _waitForContentAndOpen() {
    const openWhenReady = async () => {
      try {
        // Wait for page to load
        if (document.readyState !== 'complete') {
          await new Promise(resolve => {
            window.addEventListener('load', resolve, { once: true });
          });
        }

        // Check if there are any custom elements inside that need to be ready
        const truncateElements = this._content.querySelectorAll('truncate-text');

        if (truncateElements.length > 0) {
          // Wait for truncate-text to be defined
          try {
            await customElements.whenDefined('truncate-text');
          } catch (e) {
            // If custom element doesn't exist, just continue
          }

          // Give it time to render
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        // Now open with accurate height
        this._open(false);
      } catch (error) {
        console.error('Error opening mobile-collapse:', error);
        // Fallback: just open it
        this._open(false);
      }
    };

    openWhenReady();
  }
}

customElements.define('mobile-collapse', MobileCollapse);
